import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ExternalLink, Loader2, RefreshCw } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { useSilentSsoIframe } from '../hooks/useSilentSsoIframe';
import { tokenService } from '../services/tokenService';
import {
  sacDashboardApi,
  type SacDashboardConfig,
  type SacDashboardSlug,
} from '../services/sacDashboardService';

interface SacDashboardPageProps {
  slug: SacDashboardSlug;
  fallbackTitle: string;
}

const IFRAME_IDLE_REFRESH_MS = 60_000;
const IFRAME_REFRESH_DEDUPE_MS = 1_000;
const USER_ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'wheel'] as const;
const SMART_DASHBOARD_REFRESH_PATHS = ['/smart-dashboards', '/wow-dashboard'];
const SMART_MORNING_REFRESH_PATHS = ['/smart-morning'];

function humanError(err: unknown): string {
  if (typeof err === 'object' && err !== null) {
    const maybeResponse = err as {
      response?: { data?: { detail?: unknown }; status?: number };
      message?: string;
    };
    const detail = maybeResponse.response?.data?.detail;
    if (typeof detail === 'string' && detail.trim()) return detail;
    if (maybeResponse.response?.status === 403) return 'You do not have access to this dashboard.';
    if (maybeResponse.response?.status === 503) return 'This SAC dashboard URL is not configured.';
    if (maybeResponse.message) return maybeResponse.message;
  }
  return 'Unable to load the SAC dashboard.';
}

function pathMatches(pathname: string, routePrefixes: readonly string[]): boolean {
  return routePrefixes.some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`));
}

function iframeRefreshEnabled(slug: SacDashboardSlug, pathname: string): boolean {
  if (slug === 'smart-morning') {
    return pathMatches(pathname, SMART_MORNING_REFRESH_PATHS);
  }

  if (slug === 'wow-dashboard') {
    return pathMatches(pathname, SMART_DASHBOARD_REFRESH_PATHS);
  }

  return false;
}

export default function SacDashboardPage({
  slug,
  fallbackTitle,
}: SacDashboardPageProps): React.ReactElement {
  const location = useLocation();
  const [config, setConfig] = useState<SacDashboardConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const hasDashboardRef = useRef(false);
  const lastActivityAtRef = useRef(Date.now());
  const lastIframeRefreshAtRef = useRef(0);
  const idleRefreshTimerRef = useRef<number | null>(null);
  const refreshEnabled = iframeRefreshEnabled(slug, location.pathname);

  const loadConfig = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const nextConfig = await sacDashboardApi.getConfig(slug);
      setConfig(nextConfig);
    } catch (err) {
      setConfig(null);
      setError(humanError(err));
    } finally {
      setLoading(false);
    }
  }, [slug]);

  const refreshIframe = useCallback(() => {
    const now = Date.now();
    const iframe = iframeRef.current;

    if (!refreshEnabled) return;
    if (!hasDashboardRef.current) return;
    if (!iframe?.src) return;
    if (now - lastIframeRefreshAtRef.current < IFRAME_REFRESH_DEDUPE_MS) return;

    lastIframeRefreshAtRef.current = now;
    const currentSrc = iframe.src;
    iframe.src = currentSrc;
  }, [refreshEnabled]);

  const clearIdleRefreshTimer = useCallback(() => {
    if (idleRefreshTimerRef.current === null) return;

    window.clearTimeout(idleRefreshTimerRef.current);
    idleRefreshTimerRef.current = null;
  }, []);

  const scheduleIdleRefresh = useCallback(() => {
    clearIdleRefreshTimer();
    if (!refreshEnabled) return;

    const idleForMs = Date.now() - lastActivityAtRef.current;
    const delayMs = Math.max(IFRAME_IDLE_REFRESH_MS - idleForMs, 0);

    idleRefreshTimerRef.current = window.setTimeout(() => {
      const now = Date.now();
      const currentIdleForMs = now - lastActivityAtRef.current;
      const iframeWasRecentlyRefreshed =
        now - lastIframeRefreshAtRef.current <= IFRAME_IDLE_REFRESH_MS;

      idleRefreshTimerRef.current = null;

      if (document.visibilityState !== 'visible') return;
      if (!document.hasFocus()) return;
      if (currentIdleForMs < IFRAME_IDLE_REFRESH_MS) return;
      if (iframeWasRecentlyRefreshed) return;

      refreshIframe();
    }, delayMs);
  }, [clearIdleRefreshTimer, refreshEnabled, refreshIframe]);

  const refreshIframeAfterIdle = useCallback(() => {
    if (!refreshEnabled) return;
    if (document.visibilityState !== 'visible') return;

    const now = Date.now();
    const idleForMs = now - lastActivityAtRef.current;
    const iframeWasRecentlyRefreshed =
      now - lastIframeRefreshAtRef.current <= IFRAME_IDLE_REFRESH_MS;

    if (idleForMs >= IFRAME_IDLE_REFRESH_MS && !iframeWasRecentlyRefreshed) {
      refreshIframe();
    }

    lastActivityAtRef.current = now;
    scheduleIdleRefresh();
  }, [refreshEnabled, refreshIframe, scheduleIdleRefresh]);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  useEffect(() => {
    hasDashboardRef.current = Boolean(config?.url);

    if (config?.url && refreshEnabled) {
      scheduleIdleRefresh();
    }
  }, [config?.url, refreshEnabled, scheduleIdleRefresh]);

  useEffect(() => {
    lastActivityAtRef.current = Date.now();
    lastIframeRefreshAtRef.current = 0;
    if (refreshEnabled) {
      scheduleIdleRefresh();
    } else {
      clearIdleRefreshTimer();
    }
  }, [clearIdleRefreshTimer, refreshEnabled, scheduleIdleRefresh, slug]);

  useEffect(() => {
    if (!refreshEnabled) {
      clearIdleRefreshTimer();
      return undefined;
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        refreshIframeAfterIdle();
      }
    };

    window.addEventListener('focus', refreshIframeAfterIdle);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    USER_ACTIVITY_EVENTS.forEach((eventName) => {
      document.addEventListener(eventName, refreshIframeAfterIdle, { passive: true });
    });

    return () => {
      window.removeEventListener('focus', refreshIframeAfterIdle);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      USER_ACTIVITY_EVENTS.forEach((eventName) => {
        document.removeEventListener(eventName, refreshIframeAfterIdle);
      });
      clearIdleRefreshTimer();
    };
  }, [clearIdleRefreshTimer, refreshEnabled, refreshIframeAfterIdle]);

  // Smart Insights V3 (slug `smart-finance`) embeds the beta app, not SAC — sign in silently via
  // beta's Microsoft SSO and show it full-bleed with no "Open in SAC" bar.
  const isSmartInsightsV3 = slug === 'smart-finance';
  const {
    src: iframeSrc,
    ready: iframeReady,
    onLoad: onIframeLoad,
  } = useSilentSsoIframe(config?.url, isSmartInsightsV3, tokenService.getSsoLoginHint());

  const title = config?.title ?? fallbackTitle;
  const showHeader = !isSmartInsightsV3;

  if (loading) {
    return (
      <div className="flex h-full min-h-[calc(100vh-3.25rem)] items-center justify-center bg-neutral-50">
        <div className="flex items-center gap-3 text-sm font-medium text-neutral-600">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading {title}
        </div>
      </div>
    );
  }

  if (error || !config) {
    return (
      <div className="flex h-full min-h-[calc(100vh-3.25rem)] items-center justify-center bg-neutral-50 p-6">
        <div className="w-full max-w-md rounded-lg border border-neutral-200 bg-white p-6 shadow-sm">
          <h1 className="text-lg font-semibold text-neutral-950">{title}</h1>
          <p className="mt-2 text-sm leading-6 text-neutral-600">
            {error ?? 'Dashboard configuration was not returned by the backend.'}
          </p>
          <button
            type="button"
            onClick={loadConfig}
            className="mt-5 inline-flex h-9 items-center gap-2 rounded-md border border-neutral-300 bg-white px-3 text-sm font-medium text-neutral-800 hover:bg-neutral-50"
          >
            <RefreshCw className="h-4 w-4" />
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <section className="flex h-full min-h-[calc(100vh-3.25rem)] flex-col bg-white">
      {showHeader && (
        <div className="flex h-11 flex-shrink-0 items-center justify-between border-b border-neutral-200 px-4">
          <h1 className="truncate text-sm font-semibold text-neutral-950">{config.title}</h1>
          <div className="flex items-center gap-2">
            <a
              href={config.url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex h-8 items-center gap-2 rounded-md px-2 text-xs font-medium text-neutral-600 hover:bg-neutral-100 hover:text-neutral-950"
            >
              <ExternalLink className="h-3.5 w-3.5" />
              Open in SAC
            </a>
          </div>
        </div>
      )}
      <div className="relative flex min-h-0 flex-1 flex-col">
        {iframeSrc && (
          <iframe
            ref={iframeRef}
            title={config.title}
            src={iframeSrc}
            className={`min-h-0 w-full flex-1 border-0 ${iframeReady ? '' : 'invisible'}`}
            allow="fullscreen; clipboard-read; clipboard-write"
            onFocus={refreshIframeAfterIdle}
            onLoad={onIframeLoad}
            referrerPolicy="strict-origin-when-cross-origin"
          />
        )}
        {!iframeReady && (
          <div className="absolute inset-0 flex items-center justify-center bg-white">
            <div className="flex items-center gap-3 text-sm font-medium text-neutral-600">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading {title}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
